/**
 * Shared Portfolio Context — single source of truth for account balance, positions, and orders.
 *
 * All trader panels (Equities, Bonds, ETFs, FX) read from the same broker account state.
 * A single BrokerClient and WsClient subscription prevents duplicate API calls and ensures
 * data consistency across the workspace.
 *
 * MULTI-WINDOW SYNCHRONIZATION:
 * - Uses BroadcastChannel or PostMessage for cross-window communication
 * - Real-time broadcast of portfolio updates to all windows
 * - Timestamp-based conflict resolution
 * - 5-second state reconciliation to detect drift
 * - Automatic resync on connection loss
 *
 * Key features:
 *   - Single broker instance (passed down via props, created once in Terminal)
 *   - Shared WebSocket listener (connects once, broadcasts to all subscribers)
 *   - Real-time cross-window sync (no debouncing for sync messages)
 *   - Update lock to prevent race conditions during batch operations
 *   - Memoized hooks to minimize re-renders
 *   - Efficient subscriptions (Set-based listener management)
 *   - Window identification and main/detached window tracking
 *   - Conflict resolution via timestamp ordering
 *
 * Usage:
 *   const { positions, balance, accountId, windowId, isMainWindow } = usePortfolio();
 *   const balance = useBalance();
 *   const positions = usePositions();
 *   const { isSynced } = usePortfolioSync();
 *   const otherWindowState = useWindowPortfolioState('window-2');
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { BrokerClient } from '../api/brokerClient';
import type { WsClient, WsEvent, WsStatus } from '../api/wsClient';

/**
 * Multi-window synchronization message types
 */
type SyncMessageType = 'PORTFOLIO_UPDATE' | 'FULL_STATE_SYNC' | 'CONFLICT_DETECTED' | 'HEARTBEAT' | 'SYNC_REQUEST';

interface PortfolioSyncMessage {
  type: SyncMessageType;
  windowId: string;
  timestamp: number;
  isMainWindow: boolean;
  payload?: {
    portfolio?: SharedPortfolio;
    conflictInfo?: {
      conflictTimestamp: number;
      resolutionStrategy: 'timestamp' | 'main_window_wins';
    };
  };
}

/**
 * Window-specific state tracking for multi-window sync
 */
interface WindowStateSnapshot {
  windowId: string;
  portfolio: SharedPortfolio;
  lastSyncTimestamp: number;
  isSynced: boolean;
}

/** Single balance entry from the broker (usually one per account). */
export interface BalanceRow {
  accountId: string;
  displayName?: string;
  currency?: string;
  buyingPower?: number;
  cash?: number;
  marginUsed?: number;
  totalValue?: number;
  netLiquidation?: number;
  // Legacy fields for compatibility
  account?: string;
  dayTradeOpens?: number;
  dayTradeCloses?: number;
  dayTradeLong?: number;
  dayTradeShort?: number;
  optionBuyingPower?: number;
  marginBuyingPower?: number;
  cashAvailable?: number;
  cashBalance?: number;
  equity?: number;
  [key: string]: unknown;
}

/** Position holding in the account. */
export interface Position {
  symbol: string;
  quantity: number;
  marketValue: number;
  unrealizedPnl: number;
  costBasis?: number;
  avgPrice?: number;
  // Legacy fields for compatibility
  instrument?: string;
  price?: number;
  value?: number;
  unrealizedGain?: number;
  unrealizedGainPercent?: number;
  account?: string;
  accountId?: string;
  [key: string]: unknown;
}

/** Alias for compatibility. */
export type PositionRow = Position;

/** Order record. */
export interface Order {
  clOrdId: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price?: number;
  status: string;
  filledQty?: number;
  [key: string]: unknown;
}

/** Asset class specific balance with positions and metrics. */
export interface AssetClassBalance {
  balance: number;
  positions: Position[];
  investedValue: number;
  unrealizedPnL: number;
  allocations: number; // % of total portfolio
}

/** Overall portfolio balance metrics. */
export interface OverallBalance {
  totalBalance: number;
  totalInvested: number;
  totalUnrealizedPnL: number;
  totalReturn: number; // % return
}

/** Asset class balances by type. */
export interface AssetClassBalances {
  equity: AssetClassBalance;
  fx: AssetClassBalance;
  bonds: AssetClassBalance;
  etf: AssetClassBalance;
  overall: OverallBalance;
}

/** Mutable state snapshot — updated atomically to prevent tearing. */
export interface SharedPortfolio {
  balance: BalanceRow | undefined;
  positions: Position[];
  orders: Order[];
  lastUpdate: number; // timestamp in ms
  accountId: string | undefined;
  accountLabel: string;
  assetClassBalances: AssetClassBalances;
  // Multi-window sync metadata
  windowId?: string; // Unique identifier for this window
  isMainWindow?: boolean; // True if this is the primary window
  syncStatus?: 'synced' | 'syncing' | 'drift_detected' | 'disconnected';
}

/** Public API exposed via context. */
export interface SharedPortfolioState extends SharedPortfolio {
  /** Subscribe to portfolio changes. Returns unsubscribe function. */
  subscribe: (fn: (portfolio: SharedPortfolio) => void) => () => void;

  /** Refresh positions and balance from broker. */
  refresh: () => Promise<void>;

  /** WebSocket connection status. */
  wsStatus: WsStatus;

  /** Error message if any. */
  error?: string;

  /** True when a refresh is in flight. */
  loading: boolean;

  /** Get balance for a specific asset class. */
  getAssetClassBalance: (type: 'equity' | 'fx' | 'bonds' | 'etf') => AssetClassBalance;

  /** Get overall portfolio balance metrics. */
  getOverallBalance: () => OverallBalance;

  /** Get allocation percentage for an asset class. */
  getAssetAllocation: (type: 'equity' | 'fx' | 'bonds' | 'etf') => number;

  /** Update balance for a specific asset class. Only updates that class, not overall. */
  updateAssetClassBalance: (
    type: 'equity' | 'fx' | 'bonds' | 'etf',
    balance: number,
    positions: Position[],
    investedValue: number,
    unrealizedPnL: number,
  ) => void;

  /** Legacy: set positions directly (for backward compatibility). */
  setPositions: (positions: Position[]) => void;

  /** Legacy: set balance directly (for backward compatibility). */
  setBalance: (balance: BalanceRow | undefined) => void;

  /** Legacy: set accountId directly (for backward compatibility). */
  setAccountId: (id: string | undefined) => void;

  /** Legacy: set accountLabel directly (for backward compatibility). */
  setAccountLabel: (label: string) => void;

  /** Legacy: update a single position. */
  updatePosition: (symbol: string, updates: Partial<Position>) => void;

  /** Legacy: remove a position by symbol. */
  removePosition: (symbol: string) => void;

  // Multi-window synchronization APIs
  /** Unique identifier for this window instance */
  windowId: string;

  /** True if this is the primary/main window */
  isMainWindow: boolean;

  /** Current synchronization status across windows */
  syncStatus: 'synced' | 'syncing' | 'drift_detected' | 'disconnected';

  /** Manually trigger state sync with other windows */
  triggerSync: () => void;

  /** Get another window's portfolio state (for debugging/UI display) */
  getWindowState: (windowId: string) => WindowStateSnapshot | undefined;

  /** Get all connected window states */
  getAllWindowStates: () => WindowStateSnapshot[];
}

/** For backward compatibility with existing code. */
export type SharedPortfolioContextValue = SharedPortfolioState;

const PortfolioCtx = createContext<SharedPortfolioState | null>(null);

/**
 * Hook: read the full portfolio state.
 * Triggers re-render on any portfolio change.
 */
export function usePortfolio(): SharedPortfolioState {
  const v = useContext(PortfolioCtx);
  if (!v) throw new Error('usePortfolio outside SharedPortfolioProvider');
  return v;
}

/**
 * Hook: read portfolio sync status across windows.
 * Returns sync status, whether synced, and any warning messages.
 */
export function usePortfolioSync(): {
  isSynced: () => boolean;
  syncStatus: 'synced' | 'syncing' | 'drift_detected' | 'disconnected';
  windowCount: number;
  lastSyncTime: number;
  warning?: string;
} {
  const portfolio = usePortfolio();
  const [windowCount, setWindowCount] = useState(1);
  const [lastSyncTime, setLastSyncTime] = useState(Date.now());
  const [warning, setWarning] = useState<string | undefined>();

  useEffect(() => {
    if (portfolio.syncStatus === 'disconnected') {
      setWarning('Sync lost - reconnecting...');
    } else {
      setWarning(undefined);
    }

    if (portfolio.syncStatus === 'synced') {
      setLastSyncTime(Date.now());
    }
  }, [portfolio.syncStatus]);

  const isSynced = useCallback(() => {
    return portfolio.syncStatus === 'synced';
  }, [portfolio.syncStatus]);

  return useMemo(
    () => ({
      isSynced,
      syncStatus: portfolio.syncStatus || 'synced',
      windowCount,
      lastSyncTime,
      warning,
    }),
    [isSynced, portfolio.syncStatus, windowCount, lastSyncTime, warning],
  );
}

/**
 * Hook: read another window's portfolio state for cross-window visibility.
 */
export function useWindowPortfolioState(windowId: string): WindowStateSnapshot | undefined {
  const portfolio = usePortfolio();
  const windowState = portfolio.getWindowState?.(windowId);

  return useMemo(() => windowState, [windowState]);
}

/**
 * Legacy hook for backward compatibility.
 */
export function useSharedPortfolio(): SharedPortfolioState {
  return usePortfolio();
}

/**
 * Hook: read only the balance.
 * Triggers re-render only when balance changes.
 */
export function useBalance(): BalanceRow | undefined {
  const portfolio = usePortfolio();
  return useMemo(() => portfolio.balance, [portfolio.balance]);
}

/**
 * Hook: read only positions.
 * Triggers re-render only when positions change.
 */
export function usePositions(): Position[] {
  const portfolio = usePortfolio();
  return useMemo(() => portfolio.positions, [portfolio.positions]);
}

/**
 * Hook: read only orders.
 * Triggers re-render only when orders change.
 */
export function useOrders(): Order[] {
  const portfolio = usePortfolio();
  return useMemo(() => portfolio.orders, [portfolio.orders]);
}

/**
 * Hook: read only accountId.
 */
export function useAccountId(): string | undefined {
  const portfolio = usePortfolio();
  return useMemo(() => portfolio.accountId, [portfolio.accountId]);
}

/**
 * Hook: read only the WebSocket status.
 * Separate from portfolio so UI that only needs WS status doesn't re-render on balance changes.
 */
export function useWsStatus(): WsStatus {
  const portfolio = usePortfolio();
  return useMemo(() => portfolio.wsStatus, [portfolio.wsStatus]);
}

/**
 * Hook: read asset class balance for a specific type.
 * Triggers re-render only when that specific asset class balance changes.
 */
export function useAssetClassBalance(
  type: 'equity' | 'fx' | 'bonds' | 'etf',
): AssetClassBalance {
  const portfolio = usePortfolio();
  return useMemo(
    () => portfolio.assetClassBalances[type],
    [portfolio.assetClassBalances],
  );
}

/**
 * Hook: read overall portfolio balance metrics.
 * Triggers re-render only when overall metrics change.
 */
export function useOverallBalance(): OverallBalance {
  const portfolio = usePortfolio();
  return useMemo(
    () => portfolio.assetClassBalances.overall,
    [portfolio.assetClassBalances.overall],
  );
}

/**
 * Hook: read all asset class allocations (%).
 * Triggers re-render only when allocations change.
 */
export function useAssetAllocations(): {
  equity: number;
  fx: number;
  bonds: number;
  etf: number;
} {
  const portfolio = usePortfolio();
  return useMemo(
    () => ({
      equity: portfolio.assetClassBalances.equity.allocations,
      fx: portfolio.assetClassBalances.fx.allocations,
      bonds: portfolio.assetClassBalances.bonds.allocations,
      etf: portfolio.assetClassBalances.etf.allocations,
    }),
    [portfolio.assetClassBalances],
  );
}

interface ProviderProps {
  broker: BrokerClient;
  ws?: WsClient;
  accountId: string | undefined;
  accountLabel: string;
  brokerId: string;
  children: React.ReactNode;
  enableMultiWindowSync?: boolean; // Default: true
  windowId?: string; // If not provided, will be auto-generated
  isMainWindow?: boolean; // If not provided, will be auto-detected
}

/**
 * Generate a unique window ID based on timestamp and random value
 */
function generateWindowId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 9);
  return `window-${timestamp}-${random}`;
}

/**
 * Detect if this is the main window by checking sessionStorage
 */
function detectMainWindow(providedWindowId: string): boolean {
  if (typeof window === 'undefined') return true;

  const mainWindowId = sessionStorage.getItem('main-window-id');
  if (!mainWindowId) {
    // First window to check is the main window
    sessionStorage.setItem('main-window-id', providedWindowId);
    return true;
  }
  return mainWindowId === providedWindowId;
}

/**
 * Initialize empty asset class balances.
 */
function initializeAssetClassBalances(): AssetClassBalances {
  return {
    equity: {
      balance: 0,
      positions: [],
      investedValue: 0,
      unrealizedPnL: 0,
      allocations: 0,
    },
    fx: {
      balance: 0,
      positions: [],
      investedValue: 0,
      unrealizedPnL: 0,
      allocations: 0,
    },
    bonds: {
      balance: 0,
      positions: [],
      investedValue: 0,
      unrealizedPnL: 0,
      allocations: 0,
    },
    etf: {
      balance: 0,
      positions: [],
      investedValue: 0,
      unrealizedPnL: 0,
      allocations: 0,
    },
    overall: {
      totalBalance: 0,
      totalInvested: 0,
      totalUnrealizedPnL: 0,
      totalReturn: 0,
    },
  };
}

/**
 * Calculate allocations based on current balances.
 * Returns updated assetClassBalances with allocation percentages recalculated.
 */
function recalculateAllocations(
  balances: AssetClassBalances,
): AssetClassBalances {
  const totalBalance =
    balances.equity.balance +
    balances.fx.balance +
    balances.bonds.balance +
    balances.etf.balance;

  if (totalBalance === 0) {
    return {
      ...balances,
      equity: { ...balances.equity, allocations: 0 },
      fx: { ...balances.fx, allocations: 0 },
      bonds: { ...balances.bonds, allocations: 0 },
      etf: { ...balances.etf, allocations: 0 },
    };
  }

  return {
    ...balances,
    equity: {
      ...balances.equity,
      allocations: (balances.equity.balance / totalBalance) * 100,
    },
    fx: {
      ...balances.fx,
      allocations: (balances.fx.balance / totalBalance) * 100,
    },
    bonds: {
      ...balances.bonds,
      allocations: (balances.bonds.balance / totalBalance) * 100,
    },
    etf: {
      ...balances.etf,
      allocations: (balances.etf.balance / totalBalance) * 100,
    },
  };
}

/**
 * Provider — manages the single shared broker + WS subscription with multi-window sync.
 *
 * Mount this once at the app root or in Terminal, passing the broker and WS instances.
 * All child components can then use the hooks above to read portfolio data.
 *
 * Multi-window sync features:
 * - Broadcasts portfolio updates to all open windows in real-time
 * - Receives updates from other windows and merges state
 * - Detects and resolves conflicts using timestamps
 * - Periodically verifies state consistency across windows
 * - Automatically reconnects on sync failure
 */
export function SharedPortfolioProvider({
  broker,
  ws,
  accountId,
  accountLabel,
  brokerId,
  enableMultiWindowSync = true,
  windowId: providedWindowId,
  isMainWindow: providedIsMainWindow,
  children,
}: ProviderProps) {
  // Initialize window identity
  const windowIdRef = useRef(providedWindowId || generateWindowId());
  const windowId = windowIdRef.current;
  const isMainWindowRef = useRef(
    providedIsMainWindow !== undefined ? providedIsMainWindow : detectMainWindow(windowId),
  );
  const isMainWindow = isMainWindowRef.current;

  const [portfolio, setPortfolio] = useState<SharedPortfolio>({
    balance: undefined,
    positions: [],
    orders: [],
    lastUpdate: 0,
    accountId,
    accountLabel,
    assetClassBalances: initializeAssetClassBalances(),
    windowId,
    isMainWindow,
    syncStatus: 'syncing',
  });

  const [wsStatus, setWsStatus] = useState<WsStatus>('closed');
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  // Subscribers and update lock for thread safety.
  const subscribersRef = useRef<Set<(p: SharedPortfolio) => void>>(new Set());
  const updateLockRef = useRef(false);
  const lastUpdateRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  // Multi-window sync references
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const windowStatesRef = useRef<Map<string, WindowStateSnapshot>>(new Map());
  const conflictLogRef = useRef<Array<{
    timestamp: number;
    conflict: string;
    resolution: string;
  }>>([]);
  const lastReconciliationRef = useRef(0);
  const syncStatusRef = useRef<'synced' | 'syncing' | 'drift_detected' | 'disconnected'>('syncing');
  const syncConnectionRef = useRef(true);

  /**
   * Send portfolio state to other windows via BroadcastChannel (real-time, no debounce)
   */
  const broadcastToWindows = useCallback((msg: PortfolioSyncMessage) => {
    if (!enableMultiWindowSync || !broadcastChannelRef.current) return;

    try {
      broadcastChannelRef.current.postMessage(msg);
      console.debug('[Portfolio Sync] Broadcast message:', msg.type, 'from', windowId);
    } catch (e) {
      console.error('[Portfolio Sync] Broadcast failed:', e);
      syncConnectionRef.current = false;
      syncStatusRef.current = 'disconnected';
      setPortfolio((prev) => ({ ...prev, syncStatus: 'disconnected' }));
    }
  }, [enableMultiWindowSync, windowId]);

  /**
   * Resolve conflicts between simultaneous updates using timestamp ordering
   */
  const resolveConflict = useCallback(
    (
      localPortfolio: SharedPortfolio,
      incomingPortfolio: SharedPortfolio,
      incomingTimestamp: number,
    ): {
      resolved: SharedPortfolio;
      conflictDetected: boolean;
      resolution: string;
    } => {
      const localTimestamp = localPortfolio.lastUpdate || 0;
      const timeDiff = Math.abs(incomingTimestamp - localTimestamp);

      // If updates are very close in time, it's a potential conflict
      if (timeDiff < 100) {
        const resolution =
          isMainWindow && incomingTimestamp === localTimestamp
            ? 'main_window_wins'
            : incomingTimestamp > localTimestamp
              ? 'incoming_wins'
              : 'local_wins';

        const logEntry = {
          timestamp: Date.now(),
          conflict: `Simultaneous updates: local=${localTimestamp} vs incoming=${incomingTimestamp}`,
          resolution,
        };
        conflictLogRef.current.push(logEntry);
        if (conflictLogRef.current.length > 100) {
          conflictLogRef.current.shift();
        }

        console.warn('[Portfolio Sync] Conflict resolved:', logEntry);

        if (resolution === 'incoming_wins' || resolution === 'main_window_wins') {
          return {
            resolved: incomingPortfolio,
            conflictDetected: true,
            resolution,
          };
        }
      }

      // No conflict, use incoming if it's newer
      if (incomingTimestamp > localTimestamp) {
        return {
          resolved: incomingPortfolio,
          conflictDetected: false,
          resolution: 'newer_timestamp',
        };
      }

      return {
        resolved: localPortfolio,
        conflictDetected: false,
        resolution: 'local_newer',
      };
    },
    [isMainWindow],
  );

  /**
   * Verify all windows have the same state (5-second reconciliation)
   */
  const reconcileState = useCallback(
    (currentPortfolio: SharedPortfolio) => {
      const now = Date.now();
      if (now - lastReconciliationRef.current < 5000) {
        return; // Skip if reconciliation ran recently
      }

      lastReconciliationRef.current = now;

      // Main window broadcasts full state to ensure all windows are in sync
      if (isMainWindow && enableMultiWindowSync) {
        const msg: PortfolioSyncMessage = {
          type: 'FULL_STATE_SYNC',
          windowId,
          timestamp: now,
          isMainWindow: true,
          payload: {
            portfolio: currentPortfolio,
          },
        };
        broadcastToWindows(msg);
        console.debug('[Portfolio Sync] Full state sync broadcast from main window');
      }
    },
    [isMainWindow, enableMultiWindowSync, windowId, broadcastToWindows],
  );

  // Broadcast portfolio update to all subscribers (with debouncing).
  const broadcastUpdate = useCallback((newPortfolio: SharedPortfolio) => {
    setPortfolio(newPortfolio);
    subscribersRef.current.forEach((fn) => {
      try {
        fn(newPortfolio);
      } catch (e) {
        console.error('Portfolio subscriber error:', e);
      }
    });

    // Broadcast to other windows (real-time, no debounce for sync messages)
    if (enableMultiWindowSync) {
      const msg: PortfolioSyncMessage = {
        type: 'PORTFOLIO_UPDATE',
        windowId,
        timestamp: Date.now(),
        isMainWindow,
        payload: {
          portfolio: newPortfolio,
        },
      };
      broadcastToWindows(msg);

      // Periodically reconcile state across windows
      reconcileState(newPortfolio);
    }
  }, [enableMultiWindowSync, windowId, isMainWindow, broadcastToWindows, reconcileState]);

  // Debounced broadcast — max 1 update per second to avoid thrashing.
  const scheduleBroadcast = useCallback(
    (newPortfolio: SharedPortfolio) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateRef.current;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (timeSinceLastUpdate >= 1000) {
        // Enough time has passed — broadcast immediately.
        lastUpdateRef.current = now;
        broadcastUpdate(newPortfolio);
      } else {
        // Schedule broadcast after debounce period.
        const delay = 1000 - timeSinceLastUpdate;
        debounceTimerRef.current = setTimeout(() => {
          lastUpdateRef.current = Date.now();
          broadcastUpdate(newPortfolio);
        }, delay);
      }
    },
    [broadcastUpdate],
  );

  // Atomically update portfolio with lock to prevent race conditions.
  const updatePortfolio = useCallback(
    (updater: (prev: SharedPortfolio) => SharedPortfolio) => {
      if (updateLockRef.current) {
        console.warn('Portfolio update in progress — queueing');
        return;
      }

      updateLockRef.current = true;
      try {
        setPortfolio((prev) => {
          const next = updater(prev);
          scheduleBroadcast(next);
          return next;
        });
      } finally {
        updateLockRef.current = false;
      }
    },
    [scheduleBroadcast],
  );

  // Refresh positions and balance from broker.
  const refresh = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(undefined);
    try {
      const [posRes, balRes] = await Promise.all([
        broker.positions(brokerId, accountId),
        broker.balances(brokerId, accountId),
      ]);

      updatePortfolio((prev) => ({
        ...prev,
        positions: (posRes.positions ?? []) as Position[],
        balance: ((balRes.balances ?? [])[0]) as BalanceRow | undefined,
        lastUpdate: Date.now(),
      }));
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to refresh portfolio';
      setError(msg);
      console.error('Portfolio refresh error:', e);
    } finally {
      setLoading(false);
    }
  }, [broker, accountId, brokerId, updatePortfolio]);

  // Subscribe to WS events — single listener for all subscribers.
  useEffect(() => {
    if (!ws) return;
    if (!accountId) return;

    const handleWsEvent = (e: WsEvent) => {
      // On FillEmitted or PositionUpdated, refresh positions and balance.
      if (e.type === 'FillEmitted' || e.type === 'PositionUpdated') {
        console.debug('[Portfolio Sync] WS event received:', e.type);
        void refresh();
      }
    };

    const handleWsStatus = (s: WsStatus) => {
      setWsStatus(s);

      // Attempt to restore sync connection if WS reconnects
      if (s === 'open' && !syncConnectionRef.current) {
        syncConnectionRef.current = true;
        syncStatusRef.current = 'syncing';
        setPortfolio((prev) => ({ ...prev, syncStatus: 'syncing' }));
        console.debug('[Portfolio Sync] WS reconnected, resyncing state');
      }
    };

    const unsubscribeWs = ws.on(handleWsEvent);
    const unsubscribeStatus = ws.onStatus(handleWsStatus);

    void ws.connect();

    return () => {
      unsubscribeWs();
      unsubscribeStatus();
    };
  }, [ws, accountId, refresh]);

  // Multi-window synchronization setup
  useEffect(() => {
    if (!enableMultiWindowSync) return;
    if (typeof window === 'undefined') return;

    try {
      // Create BroadcastChannel for cross-window communication
      const channel = new BroadcastChannel('portfolio-sync');
      broadcastChannelRef.current = channel;

      console.debug('[Portfolio Sync] BroadcastChannel created for window:', windowId);

      // Message handler for incoming sync messages
      const handleMessage = (event: MessageEvent<PortfolioSyncMessage>) => {
        const msg = event.data;

        // Ignore messages from self
        if (msg.windowId === windowId) return;

        console.debug('[Portfolio Sync] Received message:', msg.type, 'from', msg.windowId);

        switch (msg.type) {
          case 'PORTFOLIO_UPDATE': {
            // Merge incoming update with local state
            if (msg.payload?.portfolio) {
              setPortfolio((prev) => {
                const resolved = resolveConflict(prev, msg.payload!.portfolio!, msg.timestamp);

                if (resolved.conflictDetected) {
                  setPortfolio((p) => ({
                    ...p,
                    syncStatus: 'drift_detected',
                  }));
                  console.warn('[Portfolio Sync] Conflict detected and resolved:', resolved.resolution);
                }

                // Update last sync timestamp
                const updated = {
                  ...resolved.resolved,
                  syncStatus: 'synced' as const,
                };
                windowStatesRef.current.set(msg.windowId, {
                  windowId: msg.windowId,
                  portfolio: updated,
                  lastSyncTimestamp: msg.timestamp,
                  isSynced: true,
                });

                return updated;
              });
            }
            break;
          }

          case 'FULL_STATE_SYNC': {
            // Accept full state from main window (especially on drift detection)
            if (msg.isMainWindow && msg.payload?.portfolio) {
              console.debug('[Portfolio Sync] Full state sync received from main window');
              setPortfolio((prev) => ({
                ...msg.payload!.portfolio!,
                windowId: prev.windowId,
                isMainWindow: prev.isMainWindow,
                syncStatus: 'synced',
              }));

              // Reset reconciliation timer to avoid constant syncs
              lastReconciliationRef.current = Date.now();
            }
            break;
          }

          case 'CONFLICT_DETECTED': {
            console.warn('[Portfolio Sync] Conflict notification:', msg.payload?.conflictInfo);
            setPortfolio((prev) => ({
              ...prev,
              syncStatus: 'drift_detected',
            }));
            break;
          }

          case 'HEARTBEAT': {
            // Update window state tracking
            windowStatesRef.current.set(msg.windowId, {
              windowId: msg.windowId,
              portfolio: msg.payload?.portfolio || ({} as SharedPortfolio),
              lastSyncTimestamp: msg.timestamp,
              isSynced: syncStatusRef.current === 'synced',
            });
            break;
          }

          case 'SYNC_REQUEST': {
            // Another window is requesting our state (for recovery/debugging)
            if (isMainWindow) {
              const response: PortfolioSyncMessage = {
                type: 'FULL_STATE_SYNC',
                windowId,
                timestamp: Date.now(),
                isMainWindow: true,
                payload: {
                  portfolio,
                },
              };
              broadcastToWindows(response);
            }
            break;
          }
        }
      };

      channel.addEventListener('message', handleMessage);

      // Send heartbeat periodically to notify other windows we're alive
      const heartbeatInterval = setInterval(() => {
        if (syncConnectionRef.current) {
          const msg: PortfolioSyncMessage = {
            type: 'HEARTBEAT',
            windowId,
            timestamp: Date.now(),
            isMainWindow,
            payload: {
              portfolio,
            },
          };
          broadcastToWindows(msg);
        }
      }, 3000);

      // Set initial sync status after a short delay to allow other windows to connect
      const syncStatusTimeout = setTimeout(() => {
        syncStatusRef.current = 'synced';
        setPortfolio((prev) => ({
          ...prev,
          syncStatus: 'synced',
        }));
      }, 500);

      return () => {
        clearInterval(heartbeatInterval);
        clearTimeout(syncStatusTimeout);
        channel.removeEventListener('message', handleMessage);
        channel.close();
        broadcastChannelRef.current = null;
      };
    } catch (e) {
      console.warn('[Portfolio Sync] BroadcastChannel not available:', e);
      // Graceful degradation: continue with local state only
      syncConnectionRef.current = false;
      setPortfolio((prev) => ({
        ...prev,
        syncStatus: 'disconnected',
      }));
    }
  }, [enableMultiWindowSync, windowId, isMainWindow, portfolio, broadcastToWindows, resolveConflict]);

  // Update accountId and accountLabel when they change.
  useEffect(() => {
    updatePortfolio((prev) => ({
      ...prev,
      accountId,
      accountLabel,
    }));
  }, [accountId, accountLabel, updatePortfolio]);

  // Refresh on initial mount and when accountId changes.
  useEffect(() => {
    if (!accountId) return;
    void refresh();
  }, [accountId, refresh]);

  // Legacy: direct setPositions method for backward compatibility.
  const setPositions = useCallback((positions: Position[]) => {
    updatePortfolio((prev) => ({
      ...prev,
      positions,
      lastUpdate: Date.now(),
    }));
  }, [updatePortfolio]);

  // Legacy: direct setBalance method for backward compatibility.
  const setBalance = useCallback((balance: BalanceRow | undefined) => {
    updatePortfolio((prev) => ({
      ...prev,
      balance,
      lastUpdate: Date.now(),
    }));
  }, [updatePortfolio]);

  // Legacy: set accountId directly.
  const setAccountId = useCallback((id: string | undefined) => {
    updatePortfolio((prev) => ({
      ...prev,
      accountId: id,
    }));
  }, [updatePortfolio]);

  // Legacy: set accountLabel directly.
  const setAccountLabel = useCallback((label: string) => {
    updatePortfolio((prev) => ({
      ...prev,
      accountLabel: label,
    }));
  }, [updatePortfolio]);

  // Legacy: update a single position.
  const updatePosition = useCallback((symbol: string, updates: Partial<Position>) => {
    updatePortfolio((prev) => ({
      ...prev,
      positions: prev.positions.map((pos) =>
        pos.symbol === symbol ? { ...pos, ...updates } : pos
      ),
      lastUpdate: Date.now(),
    }));
  }, [updatePortfolio]);

  // Legacy: remove a position by symbol.
  const removePosition = useCallback((symbol: string) => {
    updatePortfolio((prev) => ({
      ...prev,
      positions: prev.positions.filter((pos) => pos.symbol !== symbol),
      lastUpdate: Date.now(),
    }));
  }, [updatePortfolio]);

  // Get asset class balance for a specific type.
  const getAssetClassBalance = useCallback(
    (type: 'equity' | 'fx' | 'bonds' | 'etf'): AssetClassBalance => {
      return portfolio.assetClassBalances[type];
    },
    [portfolio.assetClassBalances],
  );

  // Get overall portfolio balance metrics.
  const getOverallBalance = useCallback((): OverallBalance => {
    return portfolio.assetClassBalances.overall;
  }, [portfolio.assetClassBalances.overall]);

  // Get allocation percentage for an asset class.
  const getAssetAllocation = useCallback(
    (type: 'equity' | 'fx' | 'bonds' | 'etf'): number => {
      return portfolio.assetClassBalances[type].allocations;
    },
    [portfolio.assetClassBalances],
  );

  // Update balance for a specific asset class and recalculate allocations.
  const updateAssetClassBalance = useCallback(
    (
      type: 'equity' | 'fx' | 'bonds' | 'etf',
      balance: number,
      positions: Position[],
      investedValue: number,
      unrealizedPnL: number,
    ) => {
      updatePortfolio((prev) => {
        const updated = {
          ...prev.assetClassBalances,
          [type]: {
            balance,
            positions,
            investedValue,
            unrealizedPnL,
            allocations: 0, // Will be recalculated
          },
        };

        // Recalculate allocations across all asset classes
        const withAllocations = recalculateAllocations(updated);

        // Update overall balance metrics
        const totalBalance =
          withAllocations.equity.balance +
          withAllocations.fx.balance +
          withAllocations.bonds.balance +
          withAllocations.etf.balance;

        const totalInvested =
          withAllocations.equity.investedValue +
          withAllocations.fx.investedValue +
          withAllocations.bonds.investedValue +
          withAllocations.etf.investedValue;

        const totalUnrealizedPnL =
          withAllocations.equity.unrealizedPnL +
          withAllocations.fx.unrealizedPnL +
          withAllocations.bonds.unrealizedPnL +
          withAllocations.etf.unrealizedPnL;

        const totalReturn =
          totalInvested > 0 ? (totalUnrealizedPnL / totalInvested) * 100 : 0;

        return {
          ...prev,
          assetClassBalances: {
            ...withAllocations,
            overall: {
              totalBalance,
              totalInvested,
              totalUnrealizedPnL,
              totalReturn,
            },
          },
          lastUpdate: Date.now(),
        };
      });
    },
    [updatePortfolio],
  );

  const subscribe = useCallback(
    (fn: (portfolio: SharedPortfolio) => void): (() => void) => {
      subscribersRef.current.add(fn);
      // Immediately call with current state.
      fn(portfolio);
      return () => subscribersRef.current.delete(fn);
    },
    [portfolio],
  );

  /**
   * Manually trigger state synchronization with other windows
   */
  const triggerSync = useCallback(() => {
    console.debug('[Portfolio Sync] Manual sync triggered');
    if (isMainWindow && enableMultiWindowSync) {
      const msg: PortfolioSyncMessage = {
        type: 'FULL_STATE_SYNC',
        windowId,
        timestamp: Date.now(),
        isMainWindow: true,
        payload: {
          portfolio,
        },
      };
      broadcastToWindows(msg);
    } else if (!isMainWindow) {
      // Detached window requests state from main
      const msg: PortfolioSyncMessage = {
        type: 'SYNC_REQUEST',
        windowId,
        timestamp: Date.now(),
        isMainWindow: false,
      };
      broadcastToWindows(msg);
    }
  }, [isMainWindow, enableMultiWindowSync, windowId, portfolio, broadcastToWindows]);

  /**
   * Get another window's portfolio state snapshot (for debugging/UI)
   */
  const getWindowState = useCallback((wId: string): WindowStateSnapshot | undefined => {
    return windowStatesRef.current.get(wId);
  }, []);

  /**
   * Get all connected window states
   */
  const getAllWindowStates = useCallback((): WindowStateSnapshot[] => {
    return Array.from(windowStatesRef.current.values());
  }, []);

  const value: SharedPortfolioState = {
    ...portfolio,
    subscribe,
    refresh,
    wsStatus,
    error,
    loading,
    getAssetClassBalance,
    getOverallBalance,
    getAssetAllocation,
    updateAssetClassBalance,
    setPositions,
    setBalance,
    setAccountId,
    setAccountLabel,
    updatePosition,
    removePosition,
    // Multi-window sync APIs
    windowId,
    isMainWindow,
    syncStatus: portfolio.syncStatus || 'synced',
    triggerSync,
    getWindowState,
    getAllWindowStates,
  };

  return (
    <PortfolioCtx.Provider value={value}>
      {children}
    </PortfolioCtx.Provider>
  );
}
